import { CloseIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    Center,
    Divider,
    Fade,
    Flex,
    FormControl,
    Icon,
    IconButton,
    Image,
    Input,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Stack,
    Text,
    Tooltip,
    useToast
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiUpload } from 'react-icons/fi';
import { IoMdCloseCircle } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { useGetBmAdAccountsCountQuery, useGetBmAdAccountsQuery } from "store/BmAccounts/bmAccountApi";
import { setAfter, setBefore, setCurrentPage } from "store/campaigns/campaignsSlice";
import {
    useFinishUploadMutation,
    useStartUploadSessionMutation, useUploadChunkMutation,
    useUploadImageMutation,
    useUploadVideoMutation,
} from 'store/driveUpload/uploadApi';
import { cancelUpload, setFiles, setIsWidgetOpen, setUploadAbortControllers, updateUploadStatus } from "store/driveUpload/uploadSlice";
import { adUplaodSchema } from "utils/schema";
import SearchAdaccountAdspresso from "./bmControl/SearchAdaccountAdspresso";

// Constants
const MAX_IMAGE_UPLOAD_SIZE_MB = 30; // Max size for images
const MAX_VIDEO_UPLOAD_SIZE_GB = 4; // Max size for videos
const MAX_IMAGE_UPLOAD_SIZE_BYTES = MAX_IMAGE_UPLOAD_SIZE_MB * 1024 * 1024;
const MAX_VIDEO_UPLOAD_SIZE_BYTES = MAX_VIDEO_UPLOAD_SIZE_GB * 1024 * 1024 * 1024;
const chunkSize = 5 * 1024 * 1024;


const UploadModal = ({ isOpen, onClose, onSuccess, mediaType }) => {

    const toast = useToast();
    const dispatch = useDispatch();
    const [localFiles, setLocalFiles] = useState([]);
    const { files } = useSelector(state => state.upload);

    const [uploadImage, { isLoading: isImageLoading }] = useUploadImageMutation();
    const [uploadVideo, { isLoading: isVideoLoading }] = useUploadVideoMutation();
    const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [startUploadSession] = useStartUploadSessionMutation();
    const [uploadChunk] = useUploadChunkMutation();
    const [finishUpload] = useFinishUploadMutation();
    const [afterValue, setAftervalue] = useState('');

    const { data: accountsCount } = useGetBmAdAccountsCountQuery();
    const totalCount = accountsCount?.summary?.total_count ?? 20;
    const isSearching = !!searchTerm?.trim();
    const effectivePageSize = isSearching ? String(totalCount) : "20";

    const { data: AdAccountData, refetch: refetchAdaccountData, isLoading: isAdAccountLoading, isFetching: isAdAccountFetching } = useGetBmAdAccountsQuery(
        {
            search: searchTerm ? searchTerm : '',
            pageSize: effectivePageSize,
            after: searchTerm ? '' : afterValue,
        },
        { skip: !isOpen }
    );

    useEffect(() => {
        if (hasSearched && searchTerm === '') {
            refetchAdaccountData();
        }
    }, [searchTerm]);

    // Formik setup
    const uploadFormik = useFormik({
        initialValues: {
            adsAccountName: [],
            files: [],
        },
        validationSchema: adUplaodSchema,
        onSubmit: async (values, { setSubmitting }) => {
            // Handle form submission
            if (values) {
                handleUploadAll(values.files);
                dispatch(setIsWidgetOpen(true));
                setLocalFiles([]);
                dispatch(setAfter(''));
                dispatch(setBefore(''));
                dispatch(setCurrentPage(0));
                localStorage.removeItem('after');
                localStorage.removeItem('before');
            }
            setSubmitting(false);
            uploadFormik.resetForm();
            setSelectedAdAccounts([]);
            onClose();
            localStorage.removeItem('selectedAdspressoAdsAccount');
            setSelectedAdAccounts([]);
            setAftervalue('');
        },
    });

    const onDrop = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            toast({
                title: "Upload Error",
                description: "The uploaded file exceeds the maximum size limit.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        const validFiles = [];
        const newLocalFiles = []; // Array to store original File objects

        for (const file of acceptedFiles) {
            if (file.type.startsWith("image/") && file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
                toast({
                    title: "Upload Error",
                    description: `The image ${file.name} exceeds the maximum size of 30 MB.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
            } else if (file.type.startsWith("video/") && file.size > MAX_VIDEO_UPLOAD_SIZE_BYTES) {
                toast({
                    title: "Upload Error",
                    description: `The video ${file.name} exceeds the maximum size of 4 GB.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
            } else {
                validFiles.push({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    thumbnail: URL.createObjectURL(file),
                    progress: 0,
                    status: "pending",
                    // file:file,
                });
                newLocalFiles.push({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    thumbnail: URL.createObjectURL(file),
                    progress: 0,
                    status: "pending",
                    file: file,
                });
            }
        }

        if (validFiles.length > 0) {
            dispatch(setFiles(validFiles));
            setLocalFiles(newLocalFiles); // Update local state with original files
            uploadFormik.setFieldValue('files', newLocalFiles);
        }
    };


    const handleAdaccountChange = (value) => {

        if (value) {
            // Check if the account is already selected
            const isAlreadySelected = selectedAdAccounts.some(
                (account) => account?.account_id === value?.account_id
            );
            if (isAlreadySelected) return;

            if (!isAlreadySelected) {
                // Prepare account details
                const accountDetails = {
                    ...value?.account,
                };

                // Add the account to the selectedAdAccounts state
                setSelectedAdAccounts((prevState) => {
                    const updatedAccounts = [accountDetails, ...prevState];

                    const accountNames = updatedAccounts.map(account => account.account_id);

                    uploadFormik.setFieldValue('adsAccountName', accountNames);
                    return updatedAccounts;
                });
            }
        }
    };

    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
    }, []);

    // Function to remove an account from selectedAdAccounts
    const handleRemoveAdAccount = (accountId) => {
        // Remove the account from the selectedAdAccounts state
        setSelectedAdAccounts((prevState) => {
            const updatedAccounts = prevState.filter(account => account.account_id !== accountId);

            const accountNames = updatedAccounts.map(account => account.account_id);

            uploadFormik.setFieldValue('adsAccountName', accountNames);

            return updatedAccounts;
        });
    };


    const handleUploadAll = async (filesToUpload) => {
        // console.log((window.navigator.onLine ? 'on' : 'off') + 'line');

        window.addEventListener('online', () => {
            toast({
                title: "You are back online!",
                status: "success",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        window.addEventListener('offline', () => {
            toast({
                title: "You are offline. Some functionality may be unavailable.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        const uploadPromises = [];

        for (const file of filesToUpload) {
            const adsAccountsArray = uploadFormik?.values?.adsAccountName.map(accountId => {
                const selectedAdAccount = selectedAdAccounts?.find(account => account?.account_id === accountId);
                return selectedAdAccount ? {
                    id: selectedAdAccount?._id,
                    adsAccountId: selectedAdAccount?.account_id,
                    adsAccountName: selectedAdAccount?.name,
                    businessName: selectedAdAccount?.business?.name,
                    businessId: selectedAdAccount?.business?.id,
                } : null;
            }).filter(account => account !== null);
            if (adsAccountsArray.length === 0) {
                toast({
                    title: "Account Error",
                    description: "Please select a valid Ads Account.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
                return;
            }

            if (file.type.startsWith("image/")) {
                const uploadImagePromise = (async () => {
                    const formData = new FormData();
                    formData.append('filename', file.file);
                    formData.append('adsAccounts', JSON.stringify(adsAccountsArray));

                    let progressInterval;

                    try {
                        const uploadDuration = 3000;
                        const interval = 100;
                        const totalSteps = uploadDuration / interval;

                        let progress = 0;
                        progressInterval = setInterval(() => {
                            if (progress < 100) {
                                progress += Math.round(100 / totalSteps);
                                adsAccountsArray.forEach(account => {
                                    dispatch(updateUploadStatus({ fileName: file.name, accountId: account.adsAccountId, accountName: account.adsAccountName, status: "uploading", progress }));
                                });
                            }
                        }, interval);

                        const response = await uploadImage({ payload: formData }).unwrap();
                        // Check if the response contains a "File already exist" message
                        const fileExists = response?.data?.some(item => item?.message === "File already exist");

                        if (fileExists) {
                            toast({
                                title: "Upload Error",
                                description: "File already exists",
                                status: "error",
                                duration: 5000,
                                isClosable: true,
                                position: "top-right",
                            });

                            // Dispatch failed status for the upload
                            adsAccountsArray.forEach(account => {
                                dispatch(updateUploadStatus({
                                    fileName: file.name,
                                    accountId: account.adsAccountId,
                                    accountName: account.adsAccountName,
                                    status: "failed",
                                    progress: 0,
                                }));
                            });
                        } else {
                            // If no error
                            onSuccess();

                            // Update status to "done" for each account
                            adsAccountsArray.forEach(account => {
                                dispatch(updateUploadStatus({
                                    fileName: file.name,
                                    accountId: account.adsAccountId,
                                    accountName: account.adsAccountName,
                                    status: "done",
                                    progress: 100,
                                }));
                            });
                        }

                        // Clear the interval regardless of success or failure
                        clearInterval(progressInterval);
                    } catch (error) {
                        clearInterval(progressInterval);
                        adsAccountsArray.forEach(account => {
                            dispatch(updateUploadStatus({ fileName: file.name, accountId: account.adsAccountId, accountName: account.adsAccountName, status: "failed", progress: 0 }));
                        });
                        toast({
                            title: "Upload Error",
                            description: error?.data?.message,
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                            position: "top-right",
                        });
                    }
                })();

                uploadPromises.push(uploadImagePromise);
            } else if (file.type.startsWith("video/")) {
                adsAccountsArray.forEach(selectedAdAccount => {
                    const uploadVideoPromise = (async () => {
                        const abortController = new AbortController();
                        const controllerKey = `${file.name}-${selectedAdAccount.adsAccountId}`;
                        dispatch(setUploadAbortControllers({ [controllerKey]: abortController }));

                        try {
                            const startSessionData = await startUploadSessionWithRetry({
                                file,
                                adsAccount: selectedAdAccount?.adsAccountId,
                                businessId: selectedAdAccount?.businessId
                            });

                            // Check for errors in start session data
                            if (startSessionData?.data?.status === 'error') {
                                const reason = startSessionData?.data?.message;
                                toast({
                                    title: "Upload Error",
                                    description: reason,
                                    status: "error",
                                    duration: 5000,
                                    isClosable: true,
                                    position: "top-right",
                                });
                                dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "failed", progress: 0, reason }));
                                return;
                            }

                            const initialProgress = 10;
                            dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "uploading", progress: initialProgress }));

                            const chunks = splitFile(file);
                            const uploadState = loadUploadState(file.name) || {};
                            let allChunksUploaded = true;

                            for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
                                if (abortController?.signal?.aborted) {
                                    dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "cancelled", progress: 0, reason: 'Upload cancelled' }));
                                    allChunksUploaded = false;
                                    break;
                                }
                                const { chunk, start, end } = chunks[chunkIndex];
                                const chunkId = `${start}-${end}`;

                                if (uploadState[chunkId] === 'done') {
                                    continue;
                                }

                                try {
                                    await uploadChunkWithRetry({
                                        upload_session_id: startSessionData?.data?.upload_session_id,
                                        start_offset: start,
                                        end_offset: end,
                                        chunk,
                                        adsAccount: selectedAdAccount?.adsAccountId,
                                        businessId: selectedAdAccount?.businessId,
                                        file_size: file?.size
                                    }, abortController);

                                    const progress = Math.round(((chunkIndex + 1) / chunks.length) * 90);
                                    dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "uploading", progress: initialProgress + progress }));
                                    saveUploadState(file.name, chunkId, 'done');

                                } catch (uploadError) {
                                    allChunksUploaded = false;
                                    console.error(`Chunk ${chunkIndex} failed with error: ${uploadError.message}`);
                                    chunkIndex--; // Retry the same chunk
                                }
                            }

                            if (!allChunksUploaded) {
                                dispatch(cancelUpload({ fileName: file.name, accountId: selectedAdAccount.adsAccountId }));
                            }

                            if (allChunksUploaded && !abortController.signal.aborted) {
                                await finishUpload({
                                    upload_session_id: startSessionData?.data?.upload_session_id,
                                    file,
                                    video_id: startSessionData?.data?.video_id,
                                    payload: {
                                        adsAccount: {
                                            id: selectedAdAccount?.id,
                                            adsAccountId: selectedAdAccount?.adsAccountId,
                                            adsAccountName: selectedAdAccount?.adsAccountName,
                                            businessName: selectedAdAccount?.businessName,
                                            businessId: selectedAdAccount?.businessId,
                                        }
                                    },
                                }).unwrap().then((response) => {
                                    onSuccess();
                                });
                                dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "done", progress: 100 }));
                                clearUploadState(file.name);
                            } else {
                                dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "failed", progress: 0, reason: 'Upload Cancelled' }));
                            }
                        } catch (error) {
                            dispatch(updateUploadStatus({ fileName: file.name, accountId: selectedAdAccount.adsAccountId, accountName: selectedAdAccount.adsAccountName, status: "failed", progress: 0 }));
                        }
                    })();

                    uploadPromises.push(uploadVideoPromise);
                });
            }
        }

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);
    };

    const startUploadSessionWithRetry = async (params, retries = 3) => {
        const { file, adsAccount, businessId } = params;

        for (let attempt = 0; attempt < retries; attempt++) {
            try {
                if (!navigator.onLine) {
                    await new Promise(resolve => {
                        window.addEventListener('online', () => resolve(), { once: true });
                    });
                }

                const startSessionData = await Promise.race([
                    startUploadSession({ file, adsAccount, businessId }).unwrap(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000)), // 10 seconds timeout
                ]);

                return startSessionData; // Return successful response

            } catch (error) {
                if (attempt === retries - 1) {
                    throw error; // If last attempt, throw the error
                }

                const retryDelay = Math.pow(2, attempt) * 1000; // Exponential backoff
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }
    };

    const uploadChunkWithRetry = async (params, abortController, retries = 3) => {
        const { upload_session_id, start_offset, end_offset, chunk, adsAccount, businessId, file_size } = params;

        for (let attempt = 0; attempt < retries; attempt++) {
            if (abortController.signal.aborted) {
                throw new Error('Upload cancelled');
            }

            try {
                if (!navigator.onLine) {
                    await new Promise(resolve => {
                        window.addEventListener('online', () => resolve(), { once: true });
                    });
                }

                const uploadResponse = await Promise.race([
                    uploadChunk({
                        upload_session_id,
                        start_offset,
                        end_offset,
                        chunk,
                        adsAccount,
                        businessId,
                        file_size,
                    }).unwrap(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000)), // 10 seconds timeout
                ]);

                return uploadResponse; // If upload is successful, return the response

            } catch (error) {
                if (attempt === retries - 1) {
                    throw error; // Throw error if this was the last attempt
                }

                const retryDelay = Math.pow(2, attempt) * 1000; // Exponential backoff
                await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
        }
    };

    const saveUploadState = (fileName, chunkId, status) => {
        const uploadState = JSON.parse(localStorage.getItem(`upload_state_${fileName}`)) || {};
        uploadState[chunkId] = status;
        localStorage.setItem(`upload_state_${fileName}`, JSON.stringify(uploadState));
    };

    const loadUploadState = (fileName) => {
        return JSON.parse(localStorage.getItem(`upload_state_${fileName}`)) || {};
    };

    const clearUploadState = (fileName) => {
        localStorage.removeItem(`upload_state_${fileName}`);
    };

    const splitFile = (file) => {
        const chunks = [];
        let start = 0;

        while (start < file?.file?.size) {
            const end = Math.min(start + chunkSize, file?.file?.size);
            const chunk = file?.file.slice(start, end);
            chunks.push({ start, end, chunk });
            start = end;
        }
        return chunks;
    };

    const removeFile = (index) => {
        const updatedFiles = localFiles.filter((_, i) => i !== index);
        dispatch(setFiles(updatedFiles));
        setLocalFiles(updatedFiles);
        uploadFormik.setFieldValue('files', updatedFiles);
    };


    const {
        getRootProps,
        getInputProps,
        isDragActive,
        open
    } = useDropzone({
        onDrop,
        accept: mediaType === 'image' ? {
            "image/*": [".png", ".jpg", ".jpeg"]
        } : {
            "video/*": [".mp4", ".mov", ".avi", ".gif"]
        },
        multiple: true,
        maxFiles: 50,
        maxSize: mediaType === 'image' ? MAX_IMAGE_UPLOAD_SIZE_BYTES : MAX_VIDEO_UPLOAD_SIZE_BYTES,
        disabled: !uploadFormik.values.adsAccountName.length,
        noClick: true,
    });

    const showAdAccountError = !uploadFormik.values.adsAccountName.length;

    const handleModalClose = () => {
        uploadFormik.resetForm();
        setLocalFiles([]);
        onClose();
        localStorage.removeItem('selectedAdspressoAdsAccount');
        setSelectedAdAccounts([]);
        setAftervalue('');
    };

    const handleLoadMore = (after) => {
        setAftervalue(after);
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={() => handleModalClose()} size={{ base: "5xl" }}>
                <ModalOverlay />
                <ModalContent maxH={{ base: "90vh", md: "80vh" }} w={{ base: "95vw", md: "auto" }}>
                    <ModalHeader>
                        <ModalCloseButton />
                        <Flex gap={2} direction={'column'}>
                            <Flex>
                                {mediaType === 'image' ? <Text>Upload Images</Text> : <Text>Upload Videos</Text>}
                            </Flex>
                        </Flex>
                    </ModalHeader>
                    <Divider mb={'10px'} />
                    <ModalBody overflowY="auto" maxH={{ base: "calc(90vh - 120px)", md: "calc(80vh - 120px)" }}>
                        <Flex
                            direction={{ base: "column", md: "row" }}
                            minH={{ base: "auto", md: "50vh" }}
                            gap={4}
                        >

                            {/* Ad Accounts Section - Left Side */}
                            <Box
                                flex={{ base: '100%', md: '38%' }}
                                maxH={{ base: '400px', md: "600px" }}
                                p={{ base: 1, md: 2 }}
                                >

                                {/* Display chosen accounts */}
                                <Text mb={2} fontWeight={"bold"}>
                                    Choose Ad Accounts
                                </Text>

                                <Text fontSize={'12px'} mb={2}>
                                    {mediaType === 'image' ? 'Select your Ad Accounts to upload multiple images directly to their My Drive for seamless access during Ad Campaign creation.' :
                                        'Select your Ad Accounts to upload multiple videos directly to their My Drive for seamless access during Ad Campaign creation.'}
                                </Text>

                                <SearchAdaccountAdspresso
                                    onInitialisation={() => { }}
                                    accountOptions={AdAccountData?.data}
                                    onAccountChange={handleAdaccountChange}
                                    onSearchInputChange={handleSearchChange}
                                    onSearchInputTouched={setHasSearched}
                                    AdsAccountIdFromParams={''}
                                    isAdAccountLoading={isAdAccountLoading}
                                    isAdAccountFetching={isAdAccountFetching}
                                    pageMeta={AdAccountData?.meta}
                                    onLoadMore={handleLoadMore}
                                    selectedAccounts={selectedAdAccounts}
                                    onRemoveAccount={handleRemoveAdAccount}
                                    isForStatusUpload={true}
                                />

                                <Box overflowY="auto" maxHeight="280px" pr={2} mt={2}>
                                    <Stack spacing={1} mt={2}>
                                        {selectedAdAccounts.map((account, index) => (
                                            <Flex key={`${account?.account_id}-${index}`} align="center" direction="row" gap={1}>
                                                {/* Account name and account ID with currency on separate lines */}
                                                <Flex direction="column" align="flex-start" flex={0.9} pl={1}>
                                                    <Text fontSize="12px">
                                                        {account?.name?.length > 38
                                                            ? `${account.name.substring(0, 38)}...`
                                                            : account?.name}
                                                    </Text>
                                                    <Flex gap={"5px"}>
                                                        <Text fontSize="11px" color="gray.500">
                                                            {account?.account_id}
                                                        </Text>
                                                    </Flex>
                                                </Flex>

                                                {/* Remove button */}
                                                <Flex justify="flex-end" flex={0.1}>
                                                    <Tooltip label="Remove" aria-label="Remove account tooltip" fontSize={'12px'}>
                                                        <IconButton
                                                            icon={<CloseIcon />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme="red"
                                                            onClick={() => handleRemoveAdAccount(account.account_id)}
                                                            aria-label="Remove account"
                                                        />
                                                    </Tooltip>
                                                </Flex>
                                            </Flex>
                                        ))}
                                    </Stack>
                                </Box>

                            </Box>

                            {/* Dropzone Section - Right Side */}
                            <Box
                                flex={{ base: '100%', md: '62%' }}
                                maxH={{ base: '300px', md: "600px" }}
                                overflowY="auto"
                                mt={{ base: 4, md: 0 }}
                                borderLeft={{ base: 'none', md: files.length > 0 ? '1px' : 'none' }}
                                borderColor={{ base: 'transparent', md: files.length > 0 ? 'gray.200' : 'transparent' }}
                                p={{ base: 1, md: 2 }}
                            >
                                <FormControl>
                                    {!files.length ? (
                                        <Center
                                            className="dropzone"
                                            {...getRootProps({ disabled: showAdAccountError })}
                                            sx={{
                                                textAlign: 'center',
                                                padding: { base: '10px', md: '20px' },
                                                width: '100%',
                                                margin: '0 auto',
                                                borderWidth: '1px',
                                                borderColor: 'gray.600',
                                                borderStyle: 'dashed',
                                                background: 'gray.50',
                                                borderRadius: 5,
                                                minHeight: { base: '200px', md: '360px' },
                                                cursor: showAdAccountError ? 'not-allowed' : 'default',
                                                opacity: showAdAccountError ? 0.6 : 1,
                                            }}
                                            borderColor={isDragActive ? 'gray.500' : 'gray.600'}
                                        >

                                            <Input {...getInputProps()} />
                                            <Flex flexDirection={'column'} align={'center'} justify={'center'} gap={'10px'} minW={'220px'}>
                                                <Icon as={FiUpload} fontSize="2xl" color={'gray.800'} />
                                                <Text fontSize={{ base: '10px', md: '12px' }} color={'gray.800'}>
                                                    {showAdAccountError ? (
                                                        `Select one or more Ad Accounts to upload your ${mediaType === 'image' ? 'images' : 'videos'}`
                                                    ) : (
                                                        <>
                                                            Drag 'n' drop file or
                                                            <Link textDecoration={'underline'} color={'gray.800'} onClick={open} size={'xs'} ml={1}>
                                                                Browse
                                                            </Link>
                                                        </>
                                                    )}
                                                </Text>
                                                <Text fontSize={'12px'}>{mediaType === 'image' ? `Supported file size: ≤ 30 MB` : `Supported file size: ≤ 4 GB`}</Text>

                                            </Flex>
                                        </Center>
                                    ) : (
                                        <Fade in={files.length > 0}>
                                            <Flex flexDirection="row" flexWrap={'wrap'} align="flex-start" gap="30px" minW="220px" pos="relative">
                                                {files.map((file, index) => (
                                                    <Box key={file.name} role="group" position="relative" w="80px" h={'80px'} _hover={{ cursor: "pointer", transition: "all 0.3s ease" }}>
                                                        {file?.type.startsWith("video/") ? (
                                                            <video
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '3px' }}
                                                                src={file.thumbnail}
                                                                muted
                                                            />
                                                        ) : (
                                                            <Image
                                                                src={file.thumbnail}
                                                                alt={file.name}
                                                                objectFit="cover"
                                                                width="100%"
                                                                height="100%"
                                                                borderRadius="3px"
                                                            />
                                                        )}
                                                        <Icon
                                                            top={'-5px'}
                                                            right={'-5px'}
                                                            position="absolute"
                                                            color={'red.500'}
                                                            _hover={{ color: 'gray.400' }}
                                                            as={IoMdCloseCircle}
                                                            cursor="pointer"
                                                            boxSize={4}
                                                            onClick={() => removeFile(index)}
                                                            bg={'white'}
                                                            borderRadius={'50%'}
                                                        />
                                                        <Text fontSize={'11px'} mt={'2px'} overflow={'hidden'} whiteSpace={'nowrap'} textOverflow={'ellipsis'}>{file?.name}</Text>
                                                    </Box>
                                                ))}
                                            </Flex>
                                        </Fade>
                                    )}
                                    {uploadFormik.touched.files && uploadFormik.errors.files && (
                                        <Text mt={'5px'} fontSize={'12px'} color={'red.600'}>
                                            {uploadFormik.errors.files}
                                        </Text>
                                    )}
                                </FormControl>
                            </Box>

                        </Flex>
                    </ModalBody>
                    <ModalFooter>
                        <Flex justifyContent={'flex-end'} width={'100%'} gap={'20px'}>
                            <Flex gap={4}>
                                <Button
                                    colorScheme={'gray'}
                                    variant={'solid'}
                                    size={'sm'} onClick={() => handleModalClose()}>
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme={'brand'}
                                    size={'sm'}
                                    onClick={uploadFormik.handleSubmit}
                                    isDisabled={showAdAccountError || !uploadFormik.isValid}
                                >
                                    Submit
                                </Button>

                            </Flex>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default UploadModal;